import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import TopBar from '@/components/TopBar';
import { Card, CardSm, Pill } from '@/components/ui/Form';
import { Button } from '@/components/ui/Button';
import { MapTrifold, TreeStructure, CloudArrowUp, Sparkle, Plant, ChartLineUp, Waves } from '@phosphor-icons/react';
import { fmt, healthClassToColor } from '@/lib/utils';

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get('/dashboard/summary');
      setSummary(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const seed = async () => {
    setLoading(true);
    try {
      await api.post('/dev/seed');
      await load();
    } catch (e) { console.error(e); setLoading(false); }
  };

  const c = summary?.counts || { projects: 0, plots: 0, trees: 0, measurements: 0 };
  const health = summary?.health_breakdown || {};

  return (
    <div data-testid="dashboard">
      <TopBar
        title={`Hello, ${user?.name?.split(' ')[0] || 'Inspector'}`}
        right={
          <a
            href="https://sustek.ai"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sustek-home-logo-link"
            aria-label="SusTek.ai — visit website"
            className="inline-flex items-center gap-1.5 bg-carbon text-canvas px-2 py-1.5 border-2 border-carbon hover:bg-carbon/90"
          >
            <img
              src="/sustek-white.svg"
              alt="SusTek.ai"
              data-testid="sustek-home-logo"
              className="h-6 w-auto"
            />
            <span className="font-display text-[10px] font-black uppercase tracking-widest leading-none">
              SusTek<br />.ai
            </span>
          </a>
        }
      />

      <div className="p-4 space-y-4">
        {/* Quick actions */}
        <div className="grid grid-cols-3 gap-3" data-testid="quick-actions">
          <Link to="/trees/new" data-testid="qa-start-inspection"
                className="card-hard-sm p-3 flex flex-col items-center gap-1 hover:bg-surface-alt">
            <TreeStructure size={28} weight="bold" />
            <span className="font-display text-[11px] font-bold uppercase tracking-widest text-center">Start Inspect</span>
          </Link>
          <Link to="/map" data-testid="qa-open-map"
                className="card-hard-sm p-3 flex flex-col items-center gap-1 hover:bg-surface-alt">
            <MapTrifold size={28} weight="bold" />
            <span className="font-display text-[11px] font-bold uppercase tracking-widest text-center">Plot Map</span>
          </Link>
          <Link to="/sync" data-testid="qa-sync-now"
                className="card-hard-sm p-3 flex flex-col items-center gap-1 hover:bg-surface-alt">
            <CloudArrowUp size={28} weight="bold" />
            <span className="font-display text-[11px] font-bold uppercase tracking-widest text-center">Sync Now</span>
          </Link>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 gap-3" data-testid="kpi-grid">
          <KPI label="Projects" value={c.projects} icon={<Plant size={18} weight="bold" />} />
          <KPI label="PSP Plots" value={c.plots} icon={<MapTrifold size={18} weight="bold" />} />
          <KPI label="Trees" value={c.trees} icon={<TreeStructure size={18} weight="bold" />} />
          <KPI label="Carbon (tCO₂e)"
               value={fmt(summary?.total_carbon_tco2e || 0, 3)}
               icon={<ChartLineUp size={18} weight="bold" />} />
        </div>

        {/* Health breakdown */}
        <Card data-testid="health-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold tracking-tight">Forest Health</h2>
            <Sparkle size={18} weight="bold" />
          </div>
          {Object.keys(health).length === 0 ? (
            <p className="text-sm text-carbon-soft">No measurements yet.</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(health).map(([cls, n]) => (
                <div key={cls} className="flex items-center justify-between">
                  <Pill color={healthClassToColor(cls)}>{cls}</Pill>
                  <span className="mono text-sm font-semibold">{n}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Tidal widget (mock, time-based) */}
        <CardSm data-testid="tidal-widget">
          <div className="flex items-center gap-2 mb-1">
            <Waves size={18} weight="bold" />
            <span className="font-display text-xs font-bold uppercase tracking-widest">Tide Window</span>
          </div>
          <p className="font-mono text-sm">
            Next low tide ≈ <span className="font-bold">{nextLowTideEstimate()}</span> — ideal for intertidal plots.
          </p>
        </CardSm>

        {/* Recent activity */}
        <div data-testid="recent-events">
          <h2 className="font-display font-bold tracking-tight mb-2">Recent activity</h2>
          {(summary?.recent_events || []).length === 0 ? (
            <CardSm>
              <p className="text-sm text-carbon-soft mb-3">No records yet. Seed a demo project to explore.</p>
              <Button onClick={seed} size="sm" data-testid="seed-btn">
                Load demo project
              </Button>
            </CardSm>
          ) : (
            <ul className="space-y-2">
              {summary.recent_events.map((e) => (
                <li key={e.id} className="card-hard-sm p-3 flex items-center justify-between">
                  <div>
                    <p className="font-display font-bold text-sm">
                      {e.event_type} · tree {e.tree_id?.slice(0, 6)}
                    </p>
                    <p className="text-xs mono text-carbon-soft">
                      {new Date(e.event_date).toLocaleString()}
                    </p>
                  </div>
                  {e.canopy_health_class && (
                    <Pill color={healthClassToColor(e.canopy_health_class)}>
                      {e.canopy_health_class}
                    </Pill>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <Button as={Link} to="/projects" variant="outline" className="w-full" data-testid="manage-projects-btn">
          Manage projects & plots
        </Button>
      </div>
    </div>
  );
}

function KPI({ label, value, icon }) {
  return (
    <div className="card-hard-sm p-3" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1 text-carbon-soft">
        {icon}
        <span className="font-display text-[10px] font-bold uppercase tracking-widest">{label}</span>
      </div>
      <p className="font-mono text-2xl font-bold leading-none">{value}</p>
    </div>
  );
}

function nextLowTideEstimate() {
  // Rough placeholder: tides ≈ every 12h25m; show next semidiurnal slot.
  const now = new Date();
  const baseHour = (now.getHours() + 6) % 24;
  const h = String(baseHour).padStart(2, '0');
  const m = String((now.getMinutes() + 12) % 60).padStart(2, '0');
  return `${h}:${m}`;
}
